import { useEffect, useState } from 'react';

import { Button } from '@components/button';
import { RelativeSpinner } from '@components/loading-spinner';
import { LocationSearch, LocationSearchValue } from '@components/location-search';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { getElementWidth } from '@utils/browser';

import { useCreate, useMapToken, useUpdate } from 'api/locations/hooks';
import { Translation } from 'api/locations/requests';
import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

type Props = ModalProps<{
  externalId?: string;
  location?: LocationSearchValue;
  translations?: Translation[];
  locationId?: number;
  isActive?: boolean;
  onSuccess?: (locationId: number) => void;
}>;

export const LocationModal = (props: Props) => {
  const { current, t } = useStore();
  const { closeModal } = props;
  const { showSuccess } = useToaster();

  const [translations, setTranslations] = useState<Translation[]>(
    current.langs.map((lang: CareerSiteLanguageType) => ({
      language: lang,
      title: props.translations?.find((translation) => translation.language === lang)?.title ?? '',
    })),
  );
  const [externalId, setExternalId] = useState(props.externalId ?? '');
  const [location, setLocation] = useState<LocationSearchValue | null>(props.location ?? null);
  const [width, setWidth] = useState(0);
  const [languageError, setLanguageError] = useState<Array<{ language: string; message: string }>>(
    [],
  );

  const mapTokenQuery = useMapToken();
  const isEdit = props.locationId !== undefined;
  const onSuccess = async (locationId: number) => {
    await current.loadInfo(); // To fetch the locations again for create/edit job
    showSuccess(isEdit ? t('modals.editlocation.success') : t('modals.addlocation.success'));
    closeModal();
    props.onSuccess?.(locationId);
  };
  const data = {
    externalId,
    translations,
    location,
    onSuccess,
    setLanguageError,
  };

  const update = useUpdate({ ...data, locationId: props.locationId! });
  const create = useCreate(data);

  useEffect(() => {
    setWidth(getElementWidth(document.querySelector('#modal')));
  }, []);

  const setValue = (title: string, language: CareerSiteLanguageType) => {
    setTranslations((translation) =>
      translation.map((lang) => ({
        ...lang,
        title: lang.language === language ? title : lang.title,
      })),
    );
  };

  if (!mapTokenQuery.data) {
    return <RelativeSpinner />;
  }

  return (
    <Stack space={6}>
      <Text
        font="heading1"
        text={isEdit ? t('modals.editlocation.header') : t('modals.addlocation.header')}
      />
      <Stack space={4}>
        {current.langs.map((lang: CareerSiteLanguageType) => (
          <Stack key={lang} space={1}>
            <TextField
              id={`edit-location-${lang}`}
              labelIcon={FLAG_MAPPER_SMALL[lang]}
              label={t('modals.location.label', lang.toUpperCase())}
              onChange={(title) => setValue(title, lang)}
              value={translations?.find((translation) => translation.language === lang)?.title}
              error={languageError.find((e) => e.language === lang)?.message}
            />
            <Text text={t('modals.location.helptext')} font="regular14" color="purple80" />
          </Stack>
        ))}
        <LocationSearch
          label={t('modals.location.address.label')}
          placeholder={t('modals.location.address.placeholder')}
          noLocationMatchingText={t('modals.location.address.nomatchingtext')}
          initialText={t('modals.location.address.initialtext')}
          mapToken={mapTokenQuery.data.token}
          onSelect={setLocation}
          defaultValue={location}
          width={width - 32}
          height={160}
        />
        <Stack space={1}>
          <TextField
            label={t('modals.location.externalid.label')}
            onChange={setExternalId}
            value={externalId}
          />
          <Text text={t('modals.location.externalid.helptext')} font="regular14" color="purple80" />
        </Stack>
      </Stack>
      <Stack orientation="horizontal" space={4} justify="flex-end">
        <Button variant="secondary" onClick={closeModal} text={t('common.forms.button.cancel')} />
        <Button
          isDisabled={
            translations.some((translation) => !translation.title) ||
            update.isLoading ||
            create.isLoading
          }
          onClick={() => (isEdit ? update.mutate() : create.mutate())}
          variant="primary-blue"
          text={t('common.forms.button.save')}
        />
      </Stack>
    </Stack>
  );
};
